import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";

interface DialogProps {
    open: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    className?: string;
    headerActions?: ReactNode;
}

export function Dialog({ open, onClose, title, children, className, headerActions }: DialogProps) {
    const ref = useRef<HTMLDialogElement>(null);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        if (open && !el.open) el.showModal();
        else if (!open && el.open) el.close();
    }, [open]);

    return (
        <dialog
            ref={ref}
            // A dialog opened inside this one (the ⓘ how-to) fires its own
            // close, and React bubbles it here; only our own close closes us.
            onClose={(e) => {
                if (e.target === ref.current) onClose();
            }}
            className={cn(
                // text-card-foreground is load-bearing: native <dialog> does NOT
                // inherit body color — the UA default (CanvasText = black) wins,
                // which made every dialog title/label black-on-dark in dark mode.
                "fixed inset-0 m-auto rounded-xl border border-border/70 bg-card text-card-foreground p-0 shadow-[var(--shadow-elevated)] max-w-lg w-full max-h-[90vh] overflow-y-auto",
                "backdrop:bg-black/50 backdrop:backdrop-blur-sm",
                className,
            )}
        >
            <div className="flex items-center justify-between gap-2 px-5 py-4 border-b sticky top-0 bg-card z-10">
                <h2 className="text-base font-semibold tracking-tight">{title}</h2>
                <div className="flex items-center gap-2">
                    {headerActions}
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close preview"
                        className="-mr-1.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg hover:bg-secondary transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>
            </div>
            <div className="p-5">{children}</div>
        </dialog>
    );
}
